import { Container, Sprite, TextStyle, Text, IDestroyOptions } from "pixi.js";
import { CommonResourcesPackage } from "../../../common/resources/CommonResourcesPackage";
import { IBuildingModel } from "../../../common/models/Building/IBuildingModel";
import { nameof } from "bindable-data";
import { StringFormatExtention } from "by/gamefactory/extensions/StringFormatExtention";
import { GameFont } from "../../../common/GameFont";
import { FullBuildingTooltip } from "../../../common/components/FullBuildingTooltip";
import { PiggyMultiplier } from "../../../common/components/PiggyMultiplier";

export class BuildingInfo extends Container {
    private static readonly TEXT_SPACING = 30/*px*/;
    private static readonly ICON_SPACING = 7/*px*/;
    private static readonly ICON_SIZE = 47/*px*/;

    private static readonly VISIT_TOOLTIP_TEXT = "MAXIMUM NUMBER OF GAME COINS PER VISIT";
    private static readonly LOYALTY_COINS_TOOLTIP_TEXT = "PIGGY BOOSTER,\nEXCLUSIVE TO BUILDING OWNERS";
    private static readonly COST_PER_SQUARE_TOOLTIP_TEXT = "COST PER SQUARE FEET IN USD";
    private static readonly TOTAL_SQUARE_TOOLTIP_TEXT = "TOTAL SQUARE FEET IN THE BUILDING";

    private readonly commonResources: CommonResourcesPackage;
    private readonly buildingModel: IBuildingModel;
    private readonly referenceWidth: number;

    private visitIcon: Sprite;
    private visitCostText: Text;
    private piggyIcon: PiggyMultiplier;
    private piggyText: Text;
    private costPerSqftIcon: Sprite;
    private costPerSqftText: Text;
    private sqftIcon: Sprite;
    private sqftText: Text;

    private tooltipInfo: FullBuildingTooltip;

    public constructor(commonResources: CommonResourcesPackage, buildingModel: IBuildingModel, referenceWidth: number) {
        super();
        this.commonResources = commonResources;
        this.buildingModel = buildingModel;
        this.referenceWidth = referenceWidth;
        this.buildingModel.propertyChanged.add(this.onBuildingModelPropertyChanged, this);
        this.build();
        this.layout();
    }

    private onBuildingModelPropertyChanged(model: IBuildingModel, key: string): void {
        switch (key) {
            case nameof(model, "visitCostInCoinsUpTo"):
                this.visitCostText.text = StringFormatExtention.formatWithComma(model.visitCostInCoinsUpTo.value);
                break;
            case nameof(model, "piggyMultiplier"):
                this.piggyText.text = `+${Math.round((this.buildingModel.piggyMultiplier.value - 1) * 100)}%`;
                this.piggyIcon.alpha = this.buildingModel.piggyMultiplier.value != 1 ? 1 : 0;
                this.piggyText.alpha = this.piggyIcon.alpha;
                this.piggyIcon.eventMode = this.buildingModel.piggyMultiplier.value != 1 ? "static" : "none";
                this.piggyText.eventMode = this.piggyIcon.eventMode;
                break;
            case nameof(model, "costPerSqft"):
                this.costPerSqftText.text = StringFormatExtention.formatWithComma(model.costPerSqft.value);
                break;
            case nameof(model, "sqft"):
                this.sqftText.text = StringFormatExtention.formatWithComma(model.sqft.value);
                break;
        }
    }

    private build(): void {
        this.piggyIcon = new PiggyMultiplier(this.commonResources);
        this.piggyIcon.eventMode = "static";
        this.piggyIcon.on("pointerdown", () => this.showInfoTooltip(BuildingInfo.LOYALTY_COINS_TOOLTIP_TEXT, 1, "#ffc700"));
        this.piggyIcon.scale.set(0.8);
        this.addChild(this.piggyIcon);

        this.piggyText = new Text(`+${Math.round((this.buildingModel.piggyMultiplier.value - 1) * 100)}%`, new DefaultParamsTextStyle());
        this.piggyText.eventMode = "static";
        this.piggyText.on("pointerdown", () => this.showInfoTooltip(BuildingInfo.LOYALTY_COINS_TOOLTIP_TEXT, 1, "#ffc700"));
        this.addChild(this.piggyText);

        this.visitIcon = new Sprite(this.commonResources.assets_common_ui_substract_icon_png);
        this.visitIcon.eventMode = "static";
        this.visitIcon.on("pointerdown", () => this.showInfoTooltip(BuildingInfo.VISIT_TOOLTIP_TEXT, 2, "#ffc700"));
        this.visitIcon.width = this.visitIcon.height = BuildingInfo.ICON_SIZE;
        this.addChild(this.visitIcon);

        this.visitCostText = new Text(StringFormatExtention.formatWithComma(this.buildingModel.visitCostInCoinsUpTo.value), new VisitCostTextStyle());
        this.visitCostText.eventMode = "static";
        this.visitCostText.on("pointerdown", () => this.showInfoTooltip(BuildingInfo.VISIT_TOOLTIP_TEXT, 2, "#ffc700"));
        this.addChild(this.visitCostText);

        this.costPerSqftIcon = new Sprite(this.commonResources.assets_common_ui_box_dollar_icon_png);
        this.costPerSqftIcon.eventMode = "static";
        this.costPerSqftIcon.on("pointerdown", () => this.showInfoTooltip(BuildingInfo.COST_PER_SQUARE_TOOLTIP_TEXT, 3));
        this.costPerSqftIcon.width = this.costPerSqftIcon.height = BuildingInfo.ICON_SIZE;
        this.addChild(this.costPerSqftIcon);

        this.costPerSqftText = new Text(StringFormatExtention.formatWithComma(this.buildingModel.costPerSqft.value), new DefaultParamsTextStyle());
        this.costPerSqftText.eventMode = "static";
        this.costPerSqftText.on("pointerdown", () => this.showInfoTooltip(BuildingInfo.COST_PER_SQUARE_TOOLTIP_TEXT, 3));
        this.addChild(this.costPerSqftText);

        this.sqftIcon = new Sprite(this.commonResources.assets_common_ui_box_icon_png);
        this.sqftIcon.eventMode = "static";
        this.sqftIcon.on("pointerdown", () => this.showInfoTooltip(BuildingInfo.TOTAL_SQUARE_TOOLTIP_TEXT, 4));
        this.sqftIcon.width = this.sqftIcon.height = BuildingInfo.ICON_SIZE;
        this.addChild(this.sqftIcon);

        this.sqftText = new Text(StringFormatExtention.formatWithComma(this.buildingModel.sqft.value), new DefaultParamsTextStyle());
        this.sqftText.eventMode = "static";
        this.sqftText.on("pointerdown", () => this.showInfoTooltip(BuildingInfo.TOTAL_SQUARE_TOOLTIP_TEXT, 4));
        this.addChild(this.sqftText);

        this.piggyIcon.alpha = this.buildingModel.piggyMultiplier.value != 1 ? 1 : 0;
        this.piggyText.alpha = this.piggyIcon.alpha;
        this.piggyIcon.eventMode = this.buildingModel.piggyMultiplier.value != 1 ? "static" : "none";
        this.piggyText.eventMode = this.piggyIcon.eventMode;
    }

    private layout(): void {
        const textWidth = this.visitCostText.width + this.piggyText.width + this.costPerSqftText.width + this.sqftText.width;
        const iconsWidth = 4 * BuildingInfo.ICON_SIZE;
        const spacings = 4 * BuildingInfo.ICON_SPACING + 3 * BuildingInfo.TEXT_SPACING;

        const scalePerTextItem = Math.min((this.referenceWidth - iconsWidth - spacings) / textWidth, 1);

        this.piggyText.scale.set(scalePerTextItem);
        this.piggyIcon.position.set(0, -8);
        this.piggyText.position.set(this.piggyIcon.position.x + this.piggyIcon.width + BuildingInfo.ICON_SPACING, this.piggyIcon.height * 0.5 - this.piggyText.height * 0.5 - 4);

        this.visitCostText.scale.set(scalePerTextItem);
        this.visitIcon.position.set(this.piggyText.x + this.piggyText.width + BuildingInfo.TEXT_SPACING, 0);
        this.visitCostText.position.set(this.visitIcon.position.x + this.visitIcon.width + BuildingInfo.ICON_SPACING, this.visitIcon.height * 0.5 - this.visitCostText.height * 0.5 + 2);

        this.costPerSqftText.scale.set(scalePerTextItem);
        this.costPerSqftIcon.position.set(this.visitCostText.position.x + this.visitCostText.width + BuildingInfo.TEXT_SPACING, -1);
        this.costPerSqftText.position.set(this.costPerSqftIcon.position.x + this.costPerSqftIcon.width + BuildingInfo.ICON_SPACING, this.costPerSqftIcon.height * 0.5 - this.costPerSqftText.height * 0.5 + 2);

        this.sqftText.scale.set(scalePerTextItem);
        this.sqftIcon.position.set(this.costPerSqftText.position.x + this.costPerSqftText.width + BuildingInfo.TEXT_SPACING, 0);
        this.sqftText.position.set(this.sqftIcon.position.x + this.sqftIcon.width + BuildingInfo.ICON_SPACING, this.sqftIcon.height * 0.5 - this.sqftText.height * 0.5 + 2);

        this.tooltipInfo = new FullBuildingTooltip(this.commonResources);
        this.tooltipInfo.position.set(275, -120);
        this.addChild(this.tooltipInfo);
    }

    private async showInfoTooltip(tooltipText: string, itemNumber: number, textColor: string = "#ffffff"): Promise<void> {
        if (!this.tooltipInfo.visible) {
            await this.tooltipInfo.hideHint();
        }
        this.tooltipInfo.changeTooltipTextColor(textColor);
        this.tooltipInfo.showHint(tooltipText, itemNumber);
    }

    public async hideInfoTooltip(): Promise<void> {
        await this.tooltipInfo.hideHint();
    }

    public destroy(options?: boolean | IDestroyOptions): void {
        this.buildingModel.propertyChanged.remove(this.onBuildingModelPropertyChanged, this);
        this.visitIcon.off("pointerdown");
        this.visitCostText.off("pointerdown");
        this.piggyIcon.off("pointerdown");
        this.piggyText.off("pointerdown");
        this.costPerSqftIcon.off("pointerdown");
        this.costPerSqftText.off("pointerdown");
        this.sqftIcon.off("pointerdown");
        this.sqftText.off("pointerdown");
        super.destroy(options);
    }
}

class VisitCostTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: "#FFC700",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 40,
            lineHeight: 53
        }));
    }
}

class DefaultParamsTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 40,
            lineHeight: 53
        }));
    }
}
